import { isEqual as isArraysEqual } from '../array';

export const getDepthLevel = ({ obj, key, value }) => {
  const depthLevelsMarker = new Set();
  const visitedObjects = new WeakSet();

  const getDepthLevelRecursively = (recursiveObj, currDepthLevel = 0) => {
    if (!recursiveObj) {
      return;
    }

    // Prevent infinite recursion from circular references
    if (visitedObjects.has(recursiveObj)) {
      return;
    }

    visitedObjects.add(recursiveObj);
    depthLevelsMarker.add(currDepthLevel);

    if (recursiveObj[key] && recursiveObj[key] === value) {
      return;
    }

    for (const recursiveKey in recursiveObj) {
      const currRecursiveObjValue = recursiveObj[recursiveKey];

      if (isObj(currRecursiveObjValue)) {
        getDepthLevelRecursively(currRecursiveObjValue, currDepthLevel + 1);
      }

      if (
        Array.isArray(currRecursiveObjValue) &&
        isObj(currRecursiveObjValue[0])
      ) {
        for (const subkey in currRecursiveObjValue) {
          getDepthLevelRecursively(
            currRecursiveObjValue[subkey],
            currDepthLevel + 1
          );
        }
      }
    }
  };

  getDepthLevelRecursively(obj);

  const result = [...depthLevelsMarker].pop();

  return Number(result);
};

export const getValueFromPath = (obj, path) =>
  [obj].concat(path.split('.')).reduce((prevObj, key) => prevObj[key]);

export const isEmpty = (obj) => Object.keys(obj).length === 0;

export const isEqual = (obj1, obj2, visited = new WeakMap()) => {
  if (!isObj(obj1) || !isObj(obj2)) {
    return false;
  }

  // Prevent infinite recursion from circular references
  if (visited.has(obj1)) {
    return visited.get(obj1) === obj2;
  }
  visited.set(obj1, obj2);

  if (Object.keys(obj1).length !== Object.keys(obj2).length) {
    return false;
  }

  for (const key in obj1) {
    if (obj2[key] === undefined) {
      return false;
    }

    if (isObj(obj1[key])) {
      if (!isObj(obj2[key])) {
        return false;
      }

      return isEqual(obj1[key], obj2[key], visited);
    }

    if (Array.isArray(obj1[key])) {
      if (Array.isArray(obj2[key]) || obj1[key].length !== obj2[key].length) {
        return false;
      }

      return isArraysEqual(obj1[key], obj2[key]);
    }

    if (obj1[key] !== obj2[key]) {
      return false;
    }
  }

  return true;
};

export const isObj = (item) =>
  item && typeof item === 'object' && !Array.isArray(item);

export const merge = (result, ...sources) => {
  const visited = new WeakSet();

  const mergeRecursive = (target, ...srcs) => {
    if (!srcs.length) {
      return target;
    }

    const source = srcs.shift();

    // Prevent infinite recursion from circular references
    if (visited.has(source)) {
      return target;
    }
    if (isObj(source)) {
      visited.add(source);
    }

    if (isObj(target) && isObj(source)) {
      for (const key in source) {
        if (isObj(source[key])) {
          if (target[key] === undefined) {
            target[key] = {};
          }

          mergeRecursive(target[key], source[key]);
        } else {
          target[key] = source[key];
        }
      }
    }
    return mergeRecursive(target, ...srcs);
  };

  return mergeRecursive(result, ...sources);
};

export const omit = (obj, props, result = {}, visited = new WeakSet()) => {
  if (!Array.isArray(props) || props.length === 0) {
    return obj;
  }

  if (!obj || (typeof obj !== 'object' && typeof obj !== 'function')) {
    return obj;
  }

  // Prevent infinite recursion from circular references
  if (visited.has(obj)) {
    return result;
  }
  visited.add(obj);

  for (const key in obj) {
    if (props.includes(key)) {
      continue;
    }

    if (isObj(obj[key])) {
      if (result[key] === undefined) {
        result[key] = {};
      }

      omit(obj[key], props, result[key], visited);
    } else {
      result[key] = obj[key];
    }
  }

  return result;
};

export const pick = (obj, props, result = {}, visited = new WeakSet()) => {
  if (!Array.isArray(props) || props.length === 0) {
    return obj;
  }

  if (!obj || (typeof obj !== 'object' && typeof obj !== 'function')) {
    return obj;
  }

  // Prevent infinite recursion from circular references
  if (visited.has(obj)) {
    return result;
  }
  visited.add(obj);

  for (const key in obj) {
    if (props.includes(key)) {
      result[key] = obj[key];
      continue;
    }

    if (isObj(obj[key])) {
      if (result[key] === undefined) {
        result[key] = {};
      }

      pick(obj[key], props, result[key], visited);
    }
  }

  for (const key in result) {
    if (isObj(result[key]) && isEmpty(result[key])) {
      delete result[key];
    }
  }

  return result;
};
